/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import logo from '../assets/logo.png';

const PageLoader = () => (
  <div className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center">
    <div className="relative flex items-center justify-center">
      <div className="absolute w-40 h-40 border-t-4 border-primary-green border-opacity-30 rounded-full animate-spin"></div>
      <div className="absolute w-40 h-40 border-r-4 border-primary-green rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
      <div className="z-10 flex items-center justify-center p-4 bg-white rounded-full">
         <img src={logo} alt="Kisan Plant Loading" className="w-28 sm:w-32 h-auto object-contain animate-pulse"  loading="eager" fetchpriority="high" decoding="sync" />
      </div>
    </div>
  </div>
);

const Layout = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    if (!location.state?.preventScrollReset) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }

    const seoMap = {
      '/': { 
        title: "Top Leading Sustainable Agriculture | Kisan Plant Technology", 
        description: "Kisan Plant Technology delivers innovative agri-tech, plantation, irrigation, ecosystem restoration, and sustainable land solutions across India.",
        keywords: "Kisan Plant Technology, Kisan Plant Technology Pvt Ltd, Kisan Land Development Company, Kisan Sustainable Agriculture, Kisan Sustainable Land Management"
      },
      '/about': { 
        title: "About Kisan Plant Technology| Driving Agri Innovation in India", 
        description: "Discover Kisan Plant Technology's journey in transforming agriculture through innovative farming, environmental restoration, and integrated land solutions.",
        keywords: "About Kisan Plant Technology, Kisan Plant Technology Pvt Ltd, Kisan Plant Technology, Kisan Land Development Company, Kisan Ecosystem Management Company"
      },
      '/divisions': { 
        title: "Explore AgriTech Divisions in Hyderabad | Kisan Plant Technology", 
        description: "Explore specialized divisions delivering plantation services, water management, ecosystem planning, farm development, and agri-tech innovation.",
        keywords: "Kisan Plant Technologies divisions, Farm Management Services, plantation and farming divisions, integrated agri-business group, Agricultural Business Solutions"
      },
      '/solutions': { 
        title: "Complete Agri&Land Solutions in Hyderabad |Kisan Plant Technology", 
        description: "Discover end-to-end agri and land solutions from Kisan Plant Technology, combining smart technology, water management, plantation, and ecological innovation.",
        keywords: "agricultural solutions India, smart farming and land solutions, plantation and irrigation solutions, farm management technology solutions, complete land development services"
      },
      '/solutions/timber-plantation': { 
        title: "Premium Timber Plantation Services for Lasting Growth", 
        description: "Develop profitable timber plantations across India with expert planning, species selection, plantation management, and long-term forestry solutions.",
        keywords: "timber plantation services India, commercial timber farming, teak plantation, sustainable timber forestry investment, Sandalwood Plantation"
      },
      '/solutions/horticulture': { 
        title: "Expert Horticulture Solutions for Thriving Landscapes", 
        description: "Enhance orchards, gardens, and commercial horticulture projects in Hyderabad with expert cultivation, soil management, and plant care services.",
        keywords: "horticulture farming solutions India, Fruit Plantation, commercial horticulture management, high-density fruit plantation, Horticulture Development Services"
      },
      '/solutions/smart-irrigation': { 
        title: "Trusted Advanced Smart Irrigation for Efficient Water Use", 
        description: "Install smart irrigation systems with automated controls, efficient water management, and precision solutions for modern agriculture from Kisan Plant Technology",
        keywords: "smart irrigation systems India, drip irrigation technology company, IoT-based farm irrigation, automated irrigation solutions, water-efficient farming technology"
      },
      '/solutions/farm-management': { 
        title: "Complete advanced Farm Management for Smarter Agriculture", 
        description: "Improve farm productivity across India with crop planning, field monitoring, resource optimization, and integrated farm management services.",
        keywords: "farm management services India, agricultural land management company, crop planning and monitoring, Precision Farming Solutions, end-to-end farm management solutions"
      },
      '/solutions/eco-spiritual': { 
        title: "Premium Eco Spiritual Spaces for Peaceful Natural Living", 
        description: "Create eco-spiritual spaces across India with sacred groves, meditation gardens, native plantations, and nature-inspired landscape concepts.",
        keywords: "eco-spiritual land development, spiritual retreat landscape design, sacred garden and meditation spaces, Temple Landscaping, eco-conscious spiritual estate design"
      },
      '/solutions/farmhouse-landscaping': { 
        title: "Creative Farmhouse Landscaping with Natural Beauty in Hyderabad", 
        description: "Transform farmhouses across India with customized landscaping, themed gardens, outdoor features, and sustainable plantation designs.",
        keywords: "farmhouse landscaping services, farmhouse land development company, Landscape Development, agri-residential landscape planning, farmhouse plantation and garden design"
      },
      '/solutions/land-consultation': { 
        title: "Trusted Land Consultation & Planning by Kisan Plant Technology", 
        description: "Get expert land consultation across India from Kisan Plant for site evaluation, plantation planning, land development, and agricultural projects.",
        keywords: "agricultural land consultation services, Agricultural Land Consulting, Land Development Consultancy, Agricultural Project Planning, soil and land suitability assessment"
      },
      '/projects': { 
        title: "Successful  Agriculture Projects | Kisan Plant Technology", 
        description: "View successful projects featuring landscape transformation, plantation development, irrigation infrastructure, and sustainable farming initiatives.",
        keywords: "Kisan Plant agricultural projects, Land Development Projects, farmland development projects India, irrigation project portfolio, completed agri-tech projects"
      },
      '/sustainability': { 
        title: "Sustainability & Green Farming Practices | Kisan Plant Technology", 
        description: "Build a greener future with Kisan Plant Technology through sustainable farming, intelligent irrigation, ecosystem restoration, and responsible land development.",
        keywords: "Sustainable Agriculture, sustainable agriculture solutions India, Sustainable Land Management, regenerative agriculture practices, sustainable land management company"
      },
      '/insights': { 
        title: "Latest Agri Trends & Insights | Kisan Plant Technology", 
        description: "Read the latest agriculture trends, expert opinions, project updates, and sustainable farming innovations from Kisan Plant Technology to support smarter decisions.",
        keywords: "agricultural news and insights, farming technology trends India, smart agriculture articles, plantation industry updates, agri-tech innovation insights"
      },
      '/contact': { 
        title: "Contact Kisan Plant Technology in Hyderabad | Agri Experts India", 
        description: "Contact with Kisan Plant Technology for expert consultation on plantation projects, irrigation systems, land development, and agri-tech solutions.",
        keywords: "contact Kisan Plant Technologies, Land Development Consultation, farm technology inquiry India, request land development quote, Kisan Plant customer support"
      },
      '/thank-you': { 
        title: "Thank You | Kisan Plant Technology Team", 
        description: "Thank you for contacting Kisan Plant Technology. Our team will reach out soon to discuss your agriculture, plantation, and land development goals.",
        keywords: ""
      },
      '/divisions/plantiq': { 
        title: "PlantIQ | Smart Precision Farming by Kisan Plant Technology", 
        description: "PlantIQ empowers precision farming with intelligent crop monitoring, plantation analytics, automation, and data-driven farm management solutions.",
        keywords: "smart plantation management system, Plantiq plantation technology, Timber Plants Supplier, Fruit Plants Supplier, Premium Plant Supply"
      },
      '/divisions/agriq': { 
        title: "AgriQ Smart Farming Solutions for Modern Agriculture in Hyderabad", 
        description: "AgriQ delivers precision agriculture with crop intelligence, automation, data-driven insights, and advanced technologies to maximize farm productivity.",
        keywords: "Agriq agricultural technology solutions, advanced crop management system, precision agriculture technology India, smart farming consultancy, agri-tech decision support system"
      },
      '/divisions/irriq': { 
        title: "IrriQ Intelligent Irrigation Systems for Agriculture in Hyderabad", 
        description: "IrriQ optimizes water usage through intelligent irrigation, automated controls, efficient distribution, and resource-saving technologies for agriculture.",
        keywords: "Irriq smart irrigation systems, IoT irrigation control system, automated drip irrigation technology, farm water management technology, sensor-based irrigation scheduling"
      },
      '/divisions/secure': { 
        title: "Secure Farm Protection&Agricultural Security Systems| Kisan Plant", 
        description: "Secure safeguards agricultural assets with intelligent surveillance, remote monitoring, risk management, and reliable farm protection systems.",
        keywords: "Farm Security Solutions, agricultural land security systems, CCTV for Farms, smart farm surveillance technology, Land Security Solutions"
      },
      '/divisions/greenhabitat-360': { 
        title: "GreenHabitat 360 | Integrated Ecosystem Solutions in Hyderabad", 
        description: "GreenHabitat 360 develops green infrastructure, ecological restoration, biodiversity enhancement, and environmentally responsible development.",
        keywords: "GreenHabitat 360 eco-spiritual land, sustainable land development, Green Infrastructure, Farmhouse Landscaping, nature-integrated retreat planning"
      },
      '/divisions/farm360': { 
        title: "Kisan FARM360 | End-to-End Farm Management  in Hyderabad", 
        description: "Manage your farm from soil to yield with Kisan FARM360's end-to-end solutions: smart irrigation, plantation, crop monitoring & sustainable management.",
        keywords: "Farm 360 complete farm management, end-to-end farm management platform, integrated farm operations system, Agricultural Operations Management, all-in-one agri-management platform"
      },
      '/divisions/organiq': { 
        title: "Kisan OrganiQ | Organic Farming Solutions| Kisan Plant technology", 
        description: "Grow naturally with Kisan OrganiQ's organic farming solutions, featuring sustainable cultivation, soil health management, eco-friendly practices, and healthier crop production.",
        keywords: "organic farming solutions India, Organic Agriculture, Natural Farming Solutions, Organic Farm Management, sustainable organic farming division"
      },
      '/divisions/vedvan': { 
        title: "Kisan VedVan | Forestry & Ecological Restoration  in Hyderabad", 
        description: "VedVan restores ecosystems through agroforestry, native plantations, biodiversity conservation, and long-term environmental regeneration.",
        keywords: "Vedvan eco-spiritual living spaces, spiritual retreat land development, Spiritual Garden Development, Eco Spiritual Plantation, wellness and meditation land spaces"
      },
      '/projects/agrointel-valley': { 
        title: "India's Top Smart Agriculture Project | AgroIntel Valley", 
        description: "AgroIntel Valley by Kisan Plant showcases digital agriculture, crop intelligence, innovative technologies, and integrated land development.",
        keywords: "Agrointel Valley project case study, smart farming valley development, precision agriculture project India, Smart Agriculture Project, Kisan Plant farm project"
      },
      '/projects/soilsense-agro-systems': { 
        title: "Advanced SoilSense Agro Systems Precision Soil Management", 
        description: "SoilSense Agro Systems enhances soil performance through nutrient analysis, precision mapping, land optimization, and scientific management.",
        keywords: "Soil Testing Services, Soil Health Management, Precision Agriculture, Soil Intelligence Solutions, Smart Soil Monitoring"
      },
      '/projects/harvestsphere': { 
        title: "Innovative HarvestSphere Project | Kisan Plant Technologies", 
        description: "HarvestSphere improves harvest efficiency through modern equipment, productivity optimization, intelligent workflows, and advanced crop management.",
        keywords: "Smart Farm Management Project, Farm Management Solutions, Precision Farming Project, Agricultural Operations Management, Smart Farming Ecosystem"
      },
      '/projects/ecoyield-farms': { 
        title: "Best Smart EcoYield Farms | Sustainable Agriculture Project", 
        description: "EcoYield Farms by Kisan Plant demonstrates regenerative agriculture, resource efficiency, environmentally responsible cultivation, and resilient food production.",
        keywords: "Sustainable Farming Project, Eco Farming Solutions, Organic Farm Development, Smart Agriculture Project, High-Yield Farming"
      },
      '/projects/greenacre-ecosystem': { 
        title: "Leading GreenAcre Ecosystem Project | Kisan Plant Technology", 
        description: "Greenacre Ecosystem advances regenerative agriculture, biodiversity conservation, ecological restoration, and resilient land stewardship.",
        keywords: "GreenAcre ecosystem development project, Sustainable Land Development, Green Ecosystem Development, Plantation Development, Environmental Sustainability Project"
      },
      '/projects/aquasmart-systems': { 
        title: "Expert Smart AquaSmart Systems Project | Kisan Plant Technology", 
        description: "AquaSmart Systems improves water efficiency through automated irrigation, resource optimization, and precision water management technologies.",
        keywords: "AquaSmart irrigation project, Water Management Solutions, IoT irrigation deployment project, automated farm water systems, precision water-use efficiency project"
      },
      '/projects/greencanopy-initiative': { 
        title: "Best Premier GreenCanopy Initiative | Kisan Plant Technology", 
        description: "GreenCanopy Initiative restores natural habitats through afforestation, native plantations, ecosystem renewal, and biodiversity conservation.",
        keywords: "Tree Plantation Project, Afforestation Project, Green Landscape Development, Sustainable Plantation, Environmental Conservation Project"
      },
      '/projects/verdantliving-estates': { 
        title: "Luxury VerdantLiving Estates in Hyderabad |Kisan Plant Technology", 
        description: "Verdant Living Estates creates sustainable neighborhoods with green infrastructure, open spaces, and environmentally conscious planning.",
        keywords: "VerdantLiving estate development project, Sustainable Eco Living, Farmhouse Development, Eco-Friendly Landscaping, Sustainable Land Development"
      },
      '/projects/terraverde-living': { 
        title: "Best Signature TerraVerde Living | Kisan Plant Technology", 
        description: "TerraVerde Living integrates nature with modern lifestyles through eco-conscious planning, green infrastructure, and responsible land use.",
        keywords: "TerraVerde living community project, Sustainable Residential Development, eco-residential development project, green land living project India, integrated land and living project"
      },
      '/projects/emerald-retreat': { 
        title: "Top Exclusive Emerald Retreat Project | Kisan Plant Technology", 
        description: "Emerald Retreat offers environmentally responsible living with lush green spaces, sustainable design, and harmonious surroundings.",
        keywords: "Emerald Retreat eco-estate project, luxury land development, sustainable retreat landscape case study, private nature retreat project India, eco-luxury estate development"
      },
      '/projects/harmony-green-spaces': { 
        title: "Best Harmony Green Spaces Project | Kisan Plant Technology", 
        description: "Harmony Green Spaces enhances outdoor environments with ecological planning, green infrastructure, and sustainable environmental practices.",
        keywords: "Harmony Green Spaces project, community green space development, Sustainable Landscaping Project, Eco-Friendly Landscaping, Garden Development"
      },
      '/projects/ecosphere-residency': { 
        title: "Best EcoSphere Residency Project | Kisan Plant Technology", 
        description: "EcoSphere Residency features energy-efficient homes, eco-friendly infrastructure, and environmentally responsible residential planning.",
        keywords: "Ecosphere Residency eco-project, sustainable residential ecosystem case study, green residency development India, Green Housing Project, self-sustaining residency development"
      },
      '/projects/natura-living': { 
        title: "Top Premium Natura Living Project | Kisan Plant Technology", 
        description: "Natura Living combines sustainable architecture, natural landscapes, and eco-friendly amenities to promote healthier lifestyles.",
        keywords: "Natura Living project case study, nature-integrated residential development, Eco Living Community, green residential estate India, Sustainable Residential Development"
      },
      '/projects/pristine-habitat': { 
        title: "Best Exclusive Pristine Habitat | Kisan Plant Technology", 
        description: "Pristine Habitat encourages eco-conscious living through biodiversity, efficient resource management, and environmentally responsible design.",
        keywords: "Pristine Habitat development project, Eco Habitat Project, Green Infrastructure, Nature-Based Living, sustainable habitat development India"
      },
      '/projects/greenvista-estates': { 
        title: "Best GreenVista Estates Development | Kisan Plant Technology", 
        description: "GreenVista Estates by Kisan Plant showcases sustainable communities, eco-friendly infrastructure, and innovative environmental planning.",
        keywords: "GreenVista Estates project, Eco Estate Project, Sustainable Land Development, Farm Estate Development, Nature Living Estates"
      },
      '/projects/blossom-enclave': { 
        title: "Best Luxury Blossom Enclave Nature-Inspired Lifestyle", 
        description: "Blossom Enclave features flowering gardens, tree-lined avenues, open spaces, and thoughtfully designed surroundings for a refreshing lifestyle.",
        keywords: "Blossom Enclave project case study, floral garden residential enclave, Green Community Development, Sustainable Garden Community, Nature-Inspired Living"
      },
      '/projects/oasis-habitat': { 
        title: "Signature Oasis Habitat Project | Kisan Plant Technology", 
        description: "Oasis Habitat combines water-efficient infrastructure, shaded boulevards, natural surroundings, and modern amenities for everyday comfort.",
        keywords: "Oasis Habitat project case study, Eco Habitat Development, Sustainable Green Community, Nature Living Project, Eco-Friendly Habitat, Green Residential Development"
      },
      '/projects/greenspire-living': { 
        title: "Expert Future-Ready GreenSpire Living | Kisan Plant Technology", 
        description: "GreenSpire Living offers energy-efficient spaces, natural ventilation, abundant greenery, and wellness-focused environments for modern lifestyles.",
        keywords: "GreenSpire Living project case study, vertical green living development, Eco Lifestyle Development, Sustainable Infrastructure, Green Residential Community"
      },
      '/projects/serenity-eco-estates': { 
        title: "Top Serenity Eco Estates Peaceful Environment |Sustainable Living", 
        description: "Serenity Eco Estates creates peaceful neighborhoods with recreational areas, native vegetation, ecological balance, and quality infrastructure.",
        keywords: "Serenity Eco Estates project, Eco Estate Development, Sustainable Estate Project, Nature-Inspired Estate, Eco-Friendly Land Development"
      },
      '/projects/meadow-brooks': { 
        title: "Meadow Brooks Open Space Concept | Kisan Plant Technology", 
        description: "Meadow Brooks blends scenic meadows, outdoor recreation, tree cover, and thoughtfully planned amenities in a tranquil environment.",
        keywords: "Meadow Brooks project case study, Green Community Project, Nature-Inspired Development, Eco-Friendly Township, Sustainable Residential Project"
      },
      '/projects/willow-creek-greens': { 
        title: "Willow Creek Greens Waterfront Lifestyle | Kisan Plant Technology", 
        description: "Willow Creek Greens features creek-inspired landscapes, walking trails, open parks, and vibrant outdoor spaces designed for comfortable living.",
        keywords: "Willow Creek Greens project case study, Sustainable Residential Development, Eco-Friendly Township, Nature-Inspired Living, Sustainable Estate Development"
      },
      '/projects/devavanam-retreat': { 
        title: "Devavanam Retreat Forest-Inspired Escape | Kisan Plant Technology", 
        description: "Devavanam Retreat celebrates native forests, wellness experiences, biodiversity, and serene natural surroundings for a deeper connection with nature.",
        keywords: "Devavanam eco-spiritual retreat, sacred forest retreat development, divine garden landscape project, spiritual sanctuary land development India, Vedic-inspired retreat estate design"
      },
      '/projects/nakshatra-vana-project': { 
        title: "Nakshatra Vana Native Forest Initiative  | Kisan Plant Technology", 
        description: "Nakshatra Vana restores indigenous tree species through afforestation, habitat enrichment, ecological renewal, and environmental stewardship.",
        keywords: "Nakshatra Vana forest project, Nakshatra Garden, Sacred Tree Plantation, Astrology Garden, Spiritual Plantation Project"
      },
      '/projects/sacred-grove-sanctuary': { 
        title: "Sacred Grove Sanctuary Heritage Forest | Kisan Plant Technology", 
        description: "Sacred Grove Sanctuary protects native ecosystems with habitat preservation, forest regeneration, wildlife support, and biodiversity conservation.",
        keywords: "Sacred Grove Sanctuary project, sacred forest conservation landscape, temple grove restoration project, spiritual biodiversity sanctuary India, traditional sacred grove plantation"
      }
    };

    let seoData = seoMap[location.pathname] || {
      title: 'Top Leading Sustainable Agriculture | Kisan Plant Technology',
      description: 'Kisan Plant Technology delivers innovative agri-tech, plantation, irrigation, ecosystem restoration, and sustainable land solutions across India.',
      keywords: 'sustainable agriculture solutions India, smart farming technology company, agricultural land development company, Farm Development Company, agri-tech and irrigation solutions'
    };

    document.title = seoData.title;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', seoData.description);
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      metaDescription.content = seoData.description;
      document.head.appendChild(metaDescription);
    }

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (seoData.keywords) {
      if (metaKeywords) {
        metaKeywords.setAttribute('content', seoData.keywords);
      } else {
        metaKeywords = document.createElement('meta');
        metaKeywords.name = 'keywords';
        metaKeywords.content = seoData.keywords;
        document.head.appendChild(metaKeywords);
      }
    } else if (metaKeywords) {
      metaKeywords.remove();
    }

    let metaRobots = document.querySelector('meta[name="robots"]');
    let robotsContent = location.pathname === '/thank-you' ? 'noindex, nofollow' : 'index, follow';
    if (metaRobots) {
      metaRobots.setAttribute('content', robotsContent);
    } else {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      metaRobots.content = robotsContent;
      document.head.appendChild(metaRobots);
    }

    // Update canonical link
    let canonicalUrl = 'https://kisanplant.technology' + (location.pathname === '/' ? '' : location.pathname);
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', canonicalUrl);
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = canonicalUrl;
      document.head.appendChild(canonicalLink);
    }

    // Update Author
    let metaAuthor = document.querySelector('meta[name="author"]');
    if (metaAuthor) {
      metaAuthor.setAttribute('content', 'Kisan Plant Technology');
    } else {
      metaAuthor = document.createElement('meta');
      metaAuthor.name = 'author';
      metaAuthor.content = 'Kisan Plant Technology';
      document.head.appendChild(metaAuthor);
    }

    // Update Publisher
    let metaPublisher = document.querySelector('meta[name="publisher"]');
    if (metaPublisher) {
      metaPublisher.setAttribute('content', 'Kisan Plant Technology');
    } else {
      metaPublisher = document.createElement('meta');
      metaPublisher.name = 'publisher';
      metaPublisher.content = 'Kisan Plant Technology';
      document.head.appendChild(metaPublisher);
    }
  }, [location.pathname]);

  return (
    <>
      <div className="min-h-screen flex flex-col font-main opacity-100">
        <Header />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
      
      {/* Floating WhatsApp CTA */}
      <div className="fixed bottom-6 right-6 z-50 flex items-center justify-center">
        {/* Pulse animation effect */}
        <div className="absolute inset-0 bg-[#25D366] rounded-full animate-ping opacity-25"></div>
        <a
          href="https://wa.me/919492023333?text=Hello%20Kisan%20Plant%20Technologies,%20I%20would%20like%20to%20know%20more%20about%20your%20services."
          target="_blank"
          rel="noopener noreferrer"
          className="relative bg-[#25D366] text-white p-3 sm:p-4 rounded-full shadow-2xl hover:scale-110 transition-transform duration-300 flex items-center justify-center group"
          aria-label="Contact us on WhatsApp"
        >
          <svg
            viewBox="0 0 24 24"
            className="w-7 h-7 sm:w-9 sm:h-9 fill-current"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
          </svg>
        </a>
      </div>
      </div>
    </>
  );
};

export default Layout;
